import React from 'react';
import { Briefcase, HelpCircle, LogOut, MapPin, Settings, UserRound } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAppStore } from '@/store';

const accountLinks = [
  {
    title: 'Settings',
    href: '/settings',
    icon: Settings,
  },
  {
    title: 'Help Center',
    href: '/help',
    icon: HelpCircle,
  },
  {
    title: 'Company',
    href: '/company',
    icon: Briefcase,
  },
];

export const ProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const { user, profile, fastLogout } = useAppStore();

  const companySummary = [profile?.company_name, profile?.company_region].filter(Boolean).join(', ');

  return (
    <div className="mx-auto max-w-4xl p-4 lg:p-6">
      <div className="mb-6 rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="flex items-start gap-3 sm:gap-4">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-orange-500 text-sm font-semibold text-white shadow-sm sm:h-16 sm:w-16">
            {user?.first_name?.[0] || ''}{user?.last_name?.[0] || ''}
          </div>
          <div className="min-w-0 flex-1">
            <p className="mb-1 text-xs font-semibold uppercase tracking-[0.18em] text-orange-500 sm:text-sm">Profile</p>
            <h1 className="break-words text-xl font-semibold leading-tight text-gray-900 sm:text-2xl lg:text-[1.75rem]">
              {user?.first_name} {user?.last_name}
            </h1>
            <p className="mt-1 text-sm text-gray-600">Your account, settings, and support.</p>
          </div>
        </div>

        <div className="mt-6 grid gap-3 sm:grid-cols-3">
          <div className="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Program</p>
            <p className="mt-1 text-sm font-medium text-gray-800">{profile?.program || 'Not set'}</p>
          </div>
          <div className="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">PT Phase</p>
            <p className="mt-1 text-sm font-medium text-gray-800">{profile?.pt_phase || 'Not set'}</p>
          </div>
          <div className="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Company</p>
            <p className="mt-1 text-sm font-medium text-gray-800">{profile?.company_name || 'Not set'}</p>
            <div className="mt-1 inline-flex items-center gap-1 text-xs text-gray-500">
              <MapPin className="h-3.5 w-3.5" />
              <span>{profile?.company_region || 'Location not set'}</span>
            </div>
          </div>
        </div>

        <div className="mt-4">
          <button
            onClick={() => navigate('/profile/details')}
            className="inline-flex min-h-[44px] items-center gap-2 rounded-2xl bg-orange-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-orange-700"
          >
            <UserRound className="h-4 w-4" />
            Edit profile information
          </button>
        </div>
      </div>

      <div className="rounded-3xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="mb-4 px-2">
          <h2 className="text-lg font-semibold text-gray-900">Account tools</h2>
        </div>

        <div className="grid grid-cols-3 gap-3">
          {accountLinks.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.href}
                onClick={() => navigate(item.href)}
                className="flex min-h-[112px] flex-col items-center justify-center gap-3 rounded-2xl border border-gray-200 bg-gray-50 px-3 py-4 text-center transition-colors hover:border-orange-200 hover:bg-orange-50/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white text-orange-600 shadow-sm ring-1 ring-black/5">
                  <Icon className="h-5 w-5" />
                </div>
                <p className="text-sm font-semibold text-gray-900">{item.title}</p>
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-6 rounded-3xl border border-red-100 bg-white p-4 shadow-sm">
        <button
          onClick={fastLogout}
          className="flex w-full items-center gap-3 rounded-2xl px-4 py-4 text-left text-red-600 transition-colors hover:bg-red-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
        >
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-red-50">
            <LogOut className="h-5 w-5" />
          </div>
          <div>
            <p className="text-sm font-semibold">Logout</p>
          </div>
        </button>
      </div>
    </div>
  );
};
